import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Talk from "talkjs";
import { MSB_DEFAULT_LOGO, S3 } from "../../../constants";
import SvgIcon from "../../../parts/SvgIcon";
import { useRillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";
import {
  canViewAllOrganizationClients,
  isOrganizationMember,
} from "../../../utils/organization-permissions-helpers";
import { getTalkJsLocale } from "../../../utils/talkjs-locale";
import { parseAthletesOrganizationInfoToClientList } from "../../../utils/user-helpers";

const TALK_JS_APP_ID = "tMXnzHXl";
const TALK_JS_SECRET_KEY = "sk_test_YkD7oUFWR8MLK4AMmnm4hNZxHE7cX0m7";

// TalkJS sends straight from the browser, so tell msb-api about direct (1:1)
// messages ourselves. Group chats / community channels don't count.
const notifyCoachChatActivity = ({ me, conversation }) => {
  try {
    const participantIds = Object.keys(conversation?.participants || {});
    const guests = conversation?.custom?.guests;
    const hasGuests = guests && guests !== "[]";
    if (participantIds.length !== 2 || conversation?.subject || hasGuests)
      return;
    const athleteId = participantIds.find((id) => id !== me?.id);
    if (!athleteId) return;

    fetch("/actions/chat/coach-activity", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ athleteId }),
    }).catch(() => {});
  } catch {
    // never block sending a message
  }
};

const getAthleteInfo = (participants, athletes, userId) => {
  const athleteId = Object.keys(participants).find(
    (participantId) => participantId !== userId
  );

  if (!athleteId) return null;

  const athlete = athletes.find((athlete) => athlete._id === athleteId);

  return athlete;
};

const ChatList = ({
  userId,
  athletes = [],
  conversations = [],
  selectedConversation,
  setSelectedConversation,
  mobileChatOpened,
  setMobileChatOpened,
  classNames = "",
  handleLoadMoreConversations,
  hideSeeMoreConversations,
  unreadConversations,
  isAllowToHandleGroupChats,
  allAthletesFromOrganization,
  usersOrganization,
  activeUser,
  webAppTheme,
}) => {
  const { t } = useTranslation("coaching-portal");
  const activeUserId = activeUser._id;
  const orgLogoUrl = activeUser.avatar?.s3Path
    ? `${S3.PATH}${activeUser.avatar.s3Path}`
    : MSB_DEFAULT_LOGO;
  const referAsClients = activeUser.referAsClients;
  const searchRole = referAsClients
    ? t("athletes.roleClient")
    : t("athletes.roleAthlete");
  const [athletesFilter, setAthletesFilter] = useState("");
  const [loadOnlyUnread, setLoadOnlyUnread] = useState(false);
  const conversationsToShow = loadOnlyUnread
    ? conversations.filter((conversation) =>
        unreadConversations.includes(conversation.id)
      )
    : conversations;
  const organizationActiveUsers =
    usersOrganization && usersOrganization.organizationActiveUsers
      ? usersOrganization.organizationActiveUsers
      : [];

  const allChatsParticipants =
    allAthletesFromOrganization && usersOrganization
      ? allAthletesFromOrganization.concat(organizationActiveUsers, [
          activeUser,
        ])
      : athletes.concat([activeUser]);

  //here we are getting an array of ids of the athletes that matchs with the name of them and the word that the user is filtering in the search inpunt
  const filteredAthletes = useMemo(() => {
    const result = !athletesFilter?.length
      ? athletes
      : athletes.filter((athlete) => {
          const name = athlete.name.toLowerCase();
          const filter = athletesFilter.toLowerCase();

          return name.startsWith(filter);
        });

    return result.map((athlete) => athlete._id);
  }, [athletes, athletesFilter]);

  const openNewGroupModal = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const allGroupChats = conversationsToShow
      .filter(({ subject }) => subject)
      .map(({ subject }) => subject);

    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "create-chat-group",
        groupEdited: "",
        allGroupChats: JSON.stringify(allGroupChats),
      },
    });
  };

  const openMassMessageModal = (e) => {
    e.stopPropagation();
    e.preventDefault();

    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "mass-message",
      },
    });
  };

  const openEditGroupModal = (e, conversation) => {
    e.stopPropagation();
    e.preventDefault();
    const participants = Object.keys(conversation.participants);
    const participantsString = participants.join(",");
    const isAutoManaged = conversation.custom?.isAutoManaged === "true";
    let guestsString = "";
    if (isAutoManaged) {
      try {
        const parsed = JSON.parse(conversation.custom?.guests || "[]");
        guestsString = Array.isArray(parsed) ? parsed.join(",") : "";
      } catch {
        guestsString = "";
      }
    }

    //Here we are building an string array with the id and subject being seperate by @
    const allGroupChats = conversationsToShow
      .filter(({ id, subject }) => id && subject)
      .map(({ id, subject }) => `${id}@${subject}`);

    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "edit-chat-group",
        groupParticipants: participantsString,
        groupSubject: conversation.subject,
        groupImage: conversation.photoUrl,
        groupChatId: conversation.id,
        groupEdited: "",
        allGroupChats: JSON.stringify(allGroupChats),
        isAutoManaged: isAutoManaged ? "true" : "",
        groupGuests: guestsString,
      },
    });
  };

  return (
    <div
      className={`chats-list-container${
        classNames.length ? ` ${classNames}` : ""
      }`}
    >
      <div className="search-container">
        <div className="div-search-conversation-input">
          <input
            className="input-search-chat-conversations input-search-chat-conversations-width"
            placeholder={t("chat.searchPlaceholder", {
              role: searchRole.toLowerCase(),
            })}
            value={athletesFilter}
            type="search"
            onChange={({ target: { value } }) => setAthletesFilter(value)}
          />
        </div>
        <div className="chat-list-actions-row">
          {isAllowToHandleGroupChats && (
            <button
              type="button"
              className="add-group-chat"
              onClick={openNewGroupModal}
              data-button-type="secondary"
            >
              <img
                src="/img/svg/white-plus.svg"
                alt=""
                className="filter-none!"
              />
              {t("chat.newGroup")}
            </button>
          )}
          {isAllowToHandleGroupChats && (
            <button
              type="button"
              className="add-mass-message-chat"
              onClick={openMassMessageModal}
              data-button-type="primary"
            >
              <img
                src="/img/new-icons/send.svg"
                alt=""
                className="filter-none!"
              />
              {t("chat.massMessage.openButton")}
            </button>
          )}
          <div
            className={loadOnlyUnread ? "load-only-unread" : ""}
            onClick={() => {
              setLoadOnlyUnread(!loadOnlyUnread);
            }}
          >
            <img
              src="/img/filter_chat.svg"
              alt={t("chat.filterAlt")}
              className={
                webAppTheme === "dark" ? "filter-white" : "filter-black"
              }
            />
          </div>
        </div>
      </div>
      <div className="chats-list">
        {conversationsToShow.map((conversation, idx) => {
          const isAutoManaged = conversation.custom?.isAutoManaged === "true";
          let autoManagedGuests = [];
          if (isAutoManaged) {
            try {
              autoManagedGuests = JSON.parse(
                conversation.custom?.guests || "[]"
              );
            } catch {
              autoManagedGuests = [];
            }
          }

          const athlete = getAthleteInfo(
            conversation?.participants,
            athletes,
            userId
          );

          let senderObject = allChatsParticipants.find(
            (participant) =>
              (participant?.athleteId || participant?._id) ===
              conversation?.lastMessage?.senderId
          );

          if (
            !senderObject &&
            isAutoManaged &&
            conversation?.lastMessage?.senderId
          ) {
            senderObject = allChatsParticipants.find(
              (participant) =>
                String(participant?.athleteId || participant?._id) ===
                String(conversation.lastMessage.senderId)
            );
          }

          const lastMessage = {
            sender: {
              name: senderObject?.name || senderObject?.athleteName,
              _id: conversation?.lastMessage?.senderId,
            },
            message: conversation?.lastMessage?.text,
          };

          const groupImage = conversation?.photoUrl ?? undefined;
          const groupSubject = conversation?.subject ?? undefined;

          //not show the conversation if it isn't a group chat and the athlete doesn't exists or isn't part of the active user / organization
          //here we are excluding community chat direct conversations (because the users are members or subscribers, not athletes)
          if (
            groupSubject === undefined &&
            (!athlete || !filteredAthletes.includes(athlete._id))
          ) {
            return null;
          }
          //if the conversation is a group chat
          if (groupSubject !== undefined) {
            const isUserIncluded = !athletesFilter?.length
              ? conversation?.participants.hasOwnProperty(activeUserId) ||
                autoManagedGuests.includes(String(activeUserId))
              : filteredAthletes.some(
                  (athleteId) =>
                    conversation?.participants.hasOwnProperty(athleteId) ||
                    autoManagedGuests.includes(String(athleteId))
                );
            if (!isUserIncluded) {
              return null;
            }
          }

          const lastMessageDate = moment(
            conversation.lastMessage?.createdAt
          ).locale(activeUser?.language || "en");
          const hoursSinceLastMessage = moment().diff(lastMessageDate, "hours");

          const lastMessageString = (() => {
            const raw =
              hoursSinceLastMessage > 144
                ? lastMessageDate.format("MMM D")
                : hoursSinceLastMessage > 48
                  ? lastMessageDate.format("dddd")
                  : hoursSinceLastMessage > 24
                    ? lastMessageDate
                        .calendar(null, {
                          lastDay: `[${t("chat.yesterday")}]`,
                          sameElse: "L",
                        })
                        .replace(/ at.*/, "")
                    : lastMessageDate.format("h:mm a");
            return raw.charAt(0).toUpperCase() + raw.slice(1);
          })();

          return (
            <div
              style={{
                background:
                  selectedConversation === null
                    ? idx === 0
                      ? "var(--light-gray)"
                      : "none"
                    : selectedConversation === conversation.id ||
                        (selectedConversation &&
                          typeof selectedConversation === "object" &&
                          selectedConversation.id === conversation.id)
                      ? "var(--light-gray)"
                      : "none",
              }}
              onClick={() => {
                setSelectedConversation(conversation.id);
                setMobileChatOpened(true);
              }}
              key={idx}
              className={`chat-overview-container border-bottom-chat${
                !(
                  conversations.length > 19 &&
                    !hideSeeMoreConversations &&
                    !loadOnlyUnread
                )
                  ? " no-border"
                  : ""
              }`}
            >
              <div>
                <div className="avatar">
                  <img
                    src={
                      groupImage ||
                      (groupSubject !== undefined
                        ? orgLogoUrl
                        : getAvatarUrl(athlete, true, webAppTheme))
                    }
                    className="avatar-image"
                  />
                </div>
                <div className="information">
                  <div className="flex items-center gap-2!">
                    <span className="font-semibold text-sm ">
                      {groupSubject ? groupSubject : athlete.name}
                    </span>
                    {conversation.custom?.isAutoManaged && (
                      <img
                        src="/img/new-icons/automatic-green.svg"
                        alt="Auto-Managed"
                        className="w-3 h-3 filter-none! "
                      />
                    )}
                  </div>
                  <span>
                    {groupSubject && `${lastMessage?.sender?.name}:`}{" "}
                    {conversation.lastMessage?.text}
                  </span>
                </div>
              </div>
              <div className="last-message-time-container">
                <div className="notification-list-message-div">
                  <span className="last-message-time">{lastMessageString}</span>

                  {!unreadConversations.includes(selectedConversation) &&
                    unreadConversations.includes(conversation.id) && (
                      <div className="unread-conversation-div"></div>
                    )}
                  {groupSubject && isAllowToHandleGroupChats && (
                    <a
                      onClick={(event) => {
                        openEditGroupModal(event, conversation);
                      }}
                      className={`icon-element icon-edit-group-chat`}
                    >
                      <img
                        className={
                          webAppTheme === "dark"
                            ? "icon filter-white"
                            : "icon filter-black"
                        }
                        src="/img/new-icons/edit-pencil.svg"
                        alt={t("chat.editGroupAlt")}
                      />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {conversations.length > 39 &&
        !hideSeeMoreConversations &&
        !loadOnlyUnread ? (
          <div
            className={"chat-overview-container-see-more"}
            onClick={() => handleLoadMoreConversations()}
          >
            {t("chat.seeMoreConversations")}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ChatDisabled = ({ userId, setChatEnabled, referAsClients }) => {
  const { t } = useTranslation("coaching-portal");
  const roleList = referAsClients
    ? t("athletes.roleClients")
    : t("athletes.roleAthletes");
  const enableChat = async () => {
    setChatEnabled(true);
    submit(`/actions/user/enable-chat`, {
      method: "POST",
      body: { chatEnabled: true, userId },
    });
  };

  return (
    <div className="main-disable-chat">
      <img alt={t("chat.infoAlt")} src="/img/svg/chat_info.svg" />
      <span className="chat-title">{t("chat.inAppChat")}</span>
      <span className="chat-info">{t("chat.chatInfo", { roleList })}</span>
      <span className="span-charge clipped">{t("chat.noExtraCharge")}</span>
      <div
        className="chat-button"
        onClick={() => {
          enableChat();
        }}
      >
        {t("chat.enableChat")}
      </div>
    </div>
  );
};

function MSBChat({ initId }) {
  const context = useRillContext();
  const chatboxEl = useRef();
  const session = useRef(null);
  const chatbox = useRef(null);
  const [talkLoaded, markTalkLoaded] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emptyState, setEmptyState] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [mobileChatOpened, setMobileChatOpened] = useState(false);
  const [lastConversationId, setLastConversationId] = useState(null);
  const [hideSeeMoreConversations, setHideSeeMoreConversations] =
    useState(false);
  const [unreadConversations, setUnreadConversations] = useState([]);

  const {
    locals: {
      activeUser,
      organizationRoleName,
      query,
      allAthletesFromOrganization,
      usersOrganization,
    },
  } = context;
  const { groupEdited } = query;
  const isOrganizationUserAndAllowToViewAllMessages =
    canViewAllOrganizationClients(organizationRoleName);
  const athletesUsers = isOrganizationUserAndAllowToViewAllMessages
    ? parseAthletesOrganizationInfoToClientList(allAthletesFromOrganization)
    : activeUser.athletes;
  const isRegularCoach =
    !activeUser.organizationId &&
    activeUser.permissions &&
    activeUser.permissions?.includes("coach");
  const referAsClients = activeUser.referAsClients;
  const isAllowToHandleGroupChats =
    isRegularCoach || isOrganizationMember(organizationRoleName);
  const { t, i18n } = useTranslation("coaching-portal");
  const role = referAsClients
    ? t("athletes.roleClientLower")
    : t("athletes.roleAthleteLower");
  const roleList = referAsClients
    ? t("athletes.roleClients")
    : t("athletes.roleAthletes");

  const [chatEnabled, setChatEnabled] = useState(
    activeUser?.chatEnabled || false
  );

  const { _id: userId, name, webAppTheme = "dark" } = activeUser;

  useEffect(() => {
    if (process.browser) {
      Talk.ready.then(() => markTalkLoaded(true));
    }
  }, []);

  // Community channels (PUSH/PRESS/PERFORM, plus any other group channel) have their own
  // "Community" tab — always excluded here regardless of whether this account can access
  // community chat, otherwise they leak into this list too and show up duplicated in both
  // tabs. Matches the "pull" filter the backend/mobile side already applies unconditionally
  // (lib/utils/talkjs.js's pullFilters).
  const onlyPullFilter = {
    custom: {
      isPush: ["!=", "true"],
      isPress: ["!=", "true"],
      isPerform: ["!=", "true"],
      isChannel: ["!=", "true"],
    },
  };
  const encodedOnlyPullFilter = encodeURIComponent(
    JSON.stringify(onlyPullFilter)
  );

  const getInitConversation = async (initAthleteId, conversationList) => {
    const conversation = conversationList.find(
      (conv) => conv.participants[initAthleteId] !== undefined && !conv.subject
    );

    if (!conversation) {
      const talkJSEndpoint = `https://api.talkjs.com/v1/${TALK_JS_APP_ID}/users/${initAthleteId}/conversations?filter=${encodedOnlyPullFilter}`;
      const athleteConversations = await fetch(talkJSEndpoint, {
        headers: {
          Authorization: `Bearer ${TALK_JS_SECRET_KEY}`,
        },
      })
        .then((res) => {
          setLoading(false);
          return res.json();
        })
        .catch(() => {
          setLoading(false);
          return null;
        });
      const conversation = athleteConversations.data.find(
        (conv) => conv.participants[userId] !== undefined && !conv.subject
      );
      return conversation.id;
    } else return conversation.id;
  };

  const restartConversationsState = () => {
    setConversations([]);
    setSelectedConversation(null);
    setLastConversationId(null);
    setEmptyState(true);
  };

  const getConversations = async (reload = false, unreaded = false) => {
    if (!process.browser) {
      return;
    }
    if (unreaded) {
      setLastConversationId(null);
    }

    let talkJSEndpoint = `https://api.talkjs.com/v1/${TALK_JS_APP_ID}/users/${userId}/conversations/?filter=${encodedOnlyPullFilter}&limit=40`;

    if (lastConversationId && !reload) {
      talkJSEndpoint += `&startingAfter=${encodeURIComponent(
        lastConversationId
      )}`;
    }
    if (unreaded) {
      talkJSEndpoint += `&unreadsOnly=true`;
    }

    const conversations = await fetch(talkJSEndpoint, {
      headers: {
        Authorization: `Bearer ${TALK_JS_SECRET_KEY}`,
      },
    })
      .then((res) => {
        setLoading(false);
        return res.json();
      })
      .catch(() => {
        setLoading(false);
        return null;
      });

    if (reload && unreaded) {
      setConversations(conversations.data);
      setSelectedConversation(
        conversations.data?.length
          ? initId
            ? getInitConversation(initId, conversations.data)
            : conversations.data[0].id
          : null
      );
      if (conversations.data?.length < 40) {
        setHideSeeMoreConversations(true);
        return;
      }

      setLastConversationId(
        conversations.data[conversations.data.length - 1].id
      );
      return;
    }

    if (!conversations?.data) {
      if (unreaded) {
        restartConversationsState();
      }
      return;
    }
    setConversations((prevConversations) => {
      if (reload) {
        return conversations.data;
      }
      return [...prevConversations, ...conversations.data];
    });

    if (conversations.data.length) {
      setEmptyState(false);
      if (!selectedConversation) {
        const getValidConversation = (idx = 0) => {
          if (idx >= conversations.data.length) {
            return null;
          }

          const athlete = getAthleteInfo(
            conversations.data[idx].participants,
            athletesUsers,
            userId
          );

          //if it's a group and it isn't a community chat channel conversation, select it
          if (
            conversations.data[idx].subject &&
            !conversations.data[idx].custom?.isPush &&
            !conversations.data[idx].custom?.isPress
          ) {
            return conversations.data[idx];
          }
          if (!athlete || athletesUsers.includes(athlete._id)) {
            return getValidConversation(idx + 1);
          }
          return conversations.data[idx];
        };

        const conversation = initId
          ? await getInitConversation(initId, conversations.data)
          : getValidConversation();

        if (!conversation) {
          setEmptyState(true);
          return;
        }
        setSelectedConversation(conversation);
      }

      if (conversations.data.length < 40) {
        if (unreaded) {
          restartConversationsState();
        }
        if (reload) {
          setLastConversationId(null);
        }
        setHideSeeMoreConversations(true);
        return;
      }

      if (hideSeeMoreConversations) {
        setHideSeeMoreConversations(false);
      }

      setLastConversationId(
        conversations.data[conversations.data.length - 1].id
      );
    }
  };

  const loadChat = async () => {
    let actualSession = null;
    if (!session.current) {
      const currentUser = await new Talk.User({
        id: userId,
        name,
        role: webAppTheme === "dark" ? "msbdarktheme" : "msbwhitetheme",
        locale: getTalkJsLocale(i18n.language),
      });

      actualSession = await new Talk.Session({
        appId: TALK_JS_APP_ID,
        me: currentUser,
      });

      session.current = actualSession;
      chatbox.current = await actualSession.createChatbox(
        {
          messageField: { spellcheck: true },
        },
        {
          selected: selectedConversation || null,
        },
        { showLocationButton: false }
      );
      chatbox.current.onSendMessage(notifyCoachChatActivity);
      await chatbox.current.mount(chatboxEl.current);
      chatbox.current.select(
        typeof selectedConversation === "string"
          ? selectedConversation
          : selectedConversation?.id || null
      );

      if (initId) chatbox.current.select(selectedConversation);
      if (initId && window.innerWidth < 960) setMobileChatOpened(true);

      actualSession.unreads.onChange((unreadConversations) => {
        const conversationsIds = unreadConversations.map(
          (item) => item.lastMessage.conversation.id
        );
        setUnreadConversations(conversationsIds);
      });
      actualSession.onMessage(() => {
        getConversations(true);
      });
      getConversations(true);
    } else {
      chatbox.current.select(selectedConversation);
    }
  };

  const resetChat = () => {
    setSelectedConversation(null);
    setLastConversationId(null);
    session.current?.destroy();
    session.current = null;
    chatbox.current = null;
    loadChat();
  };

  useEffect(() => {
    if (!talkLoaded || !selectedConversation) return;

    loadChat();
  }, [talkLoaded, selectedConversation]);

  /**
   * useEffect to reload the chat with the correct theme, if the user changes it from the left bar
   * (if the user changes it when the chat is opened the page is going to reload with the groupEdited query)
   * Because with that query is easier to handle the conversations, all the logic is already done for that.
   */
  useEffect(() => {
    if (talkLoaded && !!session.current && groupEdited) {
      resetChat();
    }
  }, [webAppTheme]);

  useEffect(() => {
    if (groupEdited) {
      resetChat();
    }
  }, [groupEdited]);

  useEffect(() => {
    if (session.current) {
      session.current.currentUser.set({
        locale: getTalkJsLocale(i18n.language),
      });
    }
  }, [i18n.language]);

  useEffect(() => {
    // Hide the Intercom Chat since it overlaps the chat buttons
    window.Intercom &&
      window.Intercom("update", { hide_default_launcher: true });
    window.Intercom && window.Intercom("hide");

    // hide element from dom and adding border in the header
    const arrow = document.querySelector("#profile-dropdown picture");
    const profileBox = document.querySelector("#profile-dropdown summary");
    const options = document.querySelector("#profile-dropdown > div");
    const header = document.querySelector('header[role="main"]');
    header.style.borderBottom = "1px solid var(--white-20)";
    arrow.style.display = "none";
    profileBox.style.cursor = "initial";
    options.style.visibility = "hidden";

    return () => {
      session.current?.destroy();
      // Show again the Intercom Chat
      window.Intercom &&
        window.Intercom("update", { hide_default_launcher: false });

      // hide element from dom, remove the border in the footer and remove the mobile classes
      const arrow = document.querySelector("#profile-dropdown picture");
      const options = document.querySelector("#profile-dropdown > div");
      const pageWrapper = document.getElementById("page-wrapper");
      const coachingWrapper = document.getElementById("coaching-wrapper");
      const bodyClass = document.body;
      arrow.style.display = "block";
      options.style.visibility = "unset";
      profileBox.style.cursor = "pointer";
      header.style.borderBottom = "";
      removeMobileClasses(pageWrapper, coachingWrapper, bodyClass);
    };
  }, []);

  useEffect(() => {
    if (!chatEnabled) return;
    getConversations();
  }, [chatEnabled]);

  const handleLoadMoreConversations = async (unreaded = false) => {
    if (groupEdited) {
      submit("/actions/refresh", {
        method: "GET",
        body: {
          groupEdited: "",
        },
      });
    }
    getConversations(false, unreaded);
  };

  const [chatsListClassNames, setChatsListClassNames] = useState(
    "mobile-chat-list-shown"
  );
  const [conversationClassNames, setConversationClassNames] = useState(
    "mobile-conversation-hidden"
  );

  useEffect(() => {
    const pageWrapper = document.getElementById("page-wrapper");
    const coachingWrapper = document.getElementById("coaching-wrapper");
    const bodyClass = document.body;

    if (mobileChatOpened) {
      setConversationClassNames((prevClassName) =>
        prevClassName.replace("hidden", "shown")
      );
      setChatsListClassNames((prevClassName) =>
        prevClassName.replace("shown", "hidden")
      );

      addMobileClasses(pageWrapper, coachingWrapper, bodyClass);
    } else {
      removeMobileClasses(pageWrapper, coachingWrapper, bodyClass);
      setConversationClassNames((prevClassName) =>
        prevClassName.replace("shown", "hidden")
      );
      setChatsListClassNames((prevClassName) =>
        prevClassName.replace("hidden", "shown")
      );
    }
  }, [mobileChatOpened]);

  const addMobileClasses = (pageWrapper, coachingWrapper, body) => {
    if (pageWrapper)
      pageWrapper.classList.add("page-wrapper-mobile-chat-opened");
    if (coachingWrapper)
      coachingWrapper.classList.add("coaching-wrapper-mobile-chat-opened");
  };

  const removeMobileClasses = (pageWrapper, coachingWrapper, body) => {
    if (pageWrapper)
      pageWrapper.classList.remove("page-wrapper-mobile-chat-opened");
    if (coachingWrapper)
      coachingWrapper.classList.remove("coaching-wrapper-mobile-chat-opened");
  };

  if (!chatEnabled) {
    return (
      <div className="Main Coaching-Chat chat-disabled">
        <ChatDisabled
          userId={userId}
          setChatEnabled={setChatEnabled}
          referAsClients={referAsClients}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="Main Coaching-Chat chat-loading">
        <span>{t("chat.loading")}</span>
      </div>
    );
  }

  if (emptyState) {
    return (
      <div className="Main Coaching-Chat No-Chat">
        <div className="no-conversations">
          <div className="box-container">
            <span className="title">{t("chat.noChatsYet")}</span>
            <p className="info">
              {t("chat.noConversationsInfo", { roleList, role })}
            </p>
            <a
              type="button"
              className="button save"
              href="/coaching-portal/athletes"
            >
              {t("chat.goToList", { roleList })}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main
      className={
        mobileChatOpened
          ? "Main Coaching-Chat main-mobile-chat-opened"
          : "Main Coaching-Chat main-mobile-chat-closed"
      }
    >
      <>
        <ChatList
          conversations={conversations}
          athletes={athletesUsers}
          userId={userId}
          selectedConversation={selectedConversation}
          setSelectedConversation={setSelectedConversation}
          mobileChatOpened={mobileChatOpened}
          setMobileChatOpened={setMobileChatOpened}
          classNames={chatsListClassNames}
          handleLoadMoreConversations={handleLoadMoreConversations}
          hideSeeMoreConversations={hideSeeMoreConversations}
          unreadConversations={unreadConversations}
          isAllowToHandleGroupChats={isAllowToHandleGroupChats}
          allAthletesFromOrganization={allAthletesFromOrganization}
          usersOrganization={usersOrganization}
          activeUser={activeUser}
          webAppTheme={webAppTheme}
        />
        <div
          className={
            webAppTheme == "dark"
              ? `dark-conversation-container conversation-container ${conversationClassNames}`
              : `light-conversation-container conversation-container ${conversationClassNames}`
          }
        >
          <div
            className="only-show-mobile chat-back"
            onClick={() => setMobileChatOpened(false)}
          >
            <img
              src="/img/new-icons/arrow-square-left.svg"
              className={
                webAppTheme === "dark" ? "filter-white" : "filter-black"
              }
            />
            <span>{t("chat.back")}</span>
          </div>

          <div
            className={
              mobileChatOpened
                ? "chat-box chat-box-mobile-chat-opened"
                : "chat-box"
            }
            ref={chatboxEl}
          >
            <img src="/img/spinner.gif" width="50" height="50" />
          </div>
        </div>
      </>
    </main>
  );
}

export default MSBChat;
